import React, { useState, useEffect } from 'react';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormHelperText from '@mui/material/FormHelperText';
import { CircularProgress } from '@mui/material';
import { AutoSizer, List } from 'react-virtualized';
import { isEmpty } from '../../../utils/equality';
import AudienceMetadata from '../../../models/AudienceMetadata';
import { AudienceMetadataDisplayCondition } from './AudienceMetadataDisplayCondition';
import { isLevelCode, getLevelDisplayName } from '../../../utils/levels';

interface AudienceMetadataDisplayConditionEditorProps {
  metadata: AudienceMetadata;
  metadataValuesWithHeaderUrl: string;
  displayCondition: AudienceMetadataDisplayCondition;
  viewOnly: boolean;
  onChangeDisplayConditionCallback: (displayCondition: AudienceMetadataDisplayCondition) => void;
}

const ROWHEIGHT = 42;
const MAXROWS = 10;

export default function AudienceMetadataDisplayConditionEditor(props: AudienceMetadataDisplayConditionEditorProps): React.ReactElement {
  const { displayCondition, metadata, metadataValuesWithHeaderUrl, viewOnly, onChangeDisplayConditionCallback } = props;
  const [metadataValues, setMetadataValues] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const headerIndex = metadata.headers.indexOf(displayCondition.header);

  useEffect(() => {
    setMetadataValues([]);
    getMetadataValues(displayCondition.header);
  }, [displayCondition.header]);

  if (headerIndex < 0) {
    return (
      <FormHelperText error>
        {`Personalised Field "${displayCondition.header}" cannot be found! Add missing field under Audience tab or remove this display condition.`}
      </FormHelperText>
    );
  }

  if (isFetching && isEmpty(metadataValues)) {
    return <CircularProgress className="circular" />;
  }

  // Find the matching metadata value for the stored condition value.
  // This handles the case where the stored value is in a different format (code vs display name)
  // than the metadata values. This will be removed when v1 is deprecated.
  function getMatchingMetadataValue(storedValue: string | number | null | undefined): string | null {
    if (storedValue == null) return null;
    const storedStr = String(storedValue);

    // Direct match
    if (metadataValues.includes(storedStr)) return storedStr;

    // If stored value is a code, try to find matching display name in metadata
    if (isLevelCode(storedValue)) {
      const displayName = getLevelDisplayName(storedValue);
      if (displayName && metadataValues.includes(displayName)) return displayName;
    }

    // If stored value is a display name, try to find matching code in metadata
    // (for the rare case where metadata has codes but stored value is display name)
    const matchingCode = metadataValues.find(mv => {
      if (isLevelCode(mv)) {
        const mvDisplayName = getLevelDisplayName(mv);
        return mvDisplayName === storedStr;
      }
      return false;
    });
    if (matchingCode) return matchingCode;

    // No match found - return stored value as-is (will show as unselected)
    return storedStr;
  }

  const selectedValue = getMatchingMetadataValue(displayCondition.value);

  return (
    <FormControl>
      <FormLabel>Has the value</FormLabel>
      <RadioGroup value={selectedValue} onChange={onChangeValue}>
        {metadataValues.length > MAXROWS ? renderVirtualisedList() : renderList()}
      </RadioGroup>
    </FormControl>
  );

  function renderList() {
    return metadataValues.map((value, i) => {
      const valueToDisplay = replaceNullWithEmptyString(value);
      // Show display name for level codes
      const labelToDisplay = isLevelCode(valueToDisplay) ? (getLevelDisplayName(valueToDisplay) ?? valueToDisplay) : valueToDisplay;

      return <FormControlLabel key={i} control={<Radio value={valueToDisplay} />} label={isEmpty(labelToDisplay) ? '<blank>' : labelToDisplay} disabled={viewOnly} />;
    });
  }

  function renderVirtualisedList() {
    const AutoSizerAny = AutoSizer as any;
    const ListAny = List as any;
    return (
      <div className="metadata-values-list">
        <AutoSizerAny>
          {
            ({ width, height }) => (
              <ListAny
                width={width}
                height={height}
                rowHeight={ROWHEIGHT}
                rowCount={metadataValues.length}
                rowRenderer={renderRow}
              />
            )
          }
        </AutoSizerAny>
      </div>
    );
  }

  function renderRow({ index, style }) {
    const value = replaceNullWithEmptyString(metadataValues[index]);
    // Show display name for level codes
    const labelToDisplay = isLevelCode(value) ? (getLevelDisplayName(value) ?? value) : value;

    return (
      <FormControlLabel
        key={index}
        control={<Radio value={value} />}
        label={isEmpty(labelToDisplay) ? '<blank>' : labelToDisplay}
        disabled={viewOnly}
        style={style}
      />
    );
  }

  function onChangeValue(event: React.ChangeEvent<HTMLInputElement>, value: string) {
    onChangeDisplayConditionCallback({
      ...displayCondition,
      value
    });
  }

  function getMetadataValues(header: string) {
    // Whitelist validation: only fetch if header is in the allowed metadata headers list
    if (!metadata.headers.includes(header)) {
      return;
    }

    const url = new URL(metadataValuesWithHeaderUrl);
    url.search = new URLSearchParams({ header: header.trim() }).toString();

    const requestOptions = {
      method: 'GET'
    };

    // SSRF Safe: URL base is controlled (metadataValuesWithHeaderUrl prop from server),
    // header parameter is validated against metadata.headers whitelist above and properly
    // encoded by URLSearchParams
    setIsFetching(true);
    return fetch(url.toString(), requestOptions)
      .then(response => response.json())
      .then(data => {
        if (data?.metadata_values) {
          setMetadataValues(data.metadata_values.sort());
        }
      })
      .finally(() => setIsFetching(false));
  }

  function replaceNullWithEmptyString(value) {
    return value || '';
  }
}
